import { CourseData, SavedCourse } from "@vixi/types";
import { assert } from "../assert";
import { STEP_TYPE } from "@vixi/types";
import { getI18n } from "@vixi/localization";
import arrayToPrefixedDict from "@/arrayToPrefixedDict";

export interface CourseToSavedCourseOptions {
  ignoreId?: boolean;
}

function stepToSavedStep({ ignoreId }: CourseToSavedCourseOptions = {}) {
  return (step: CourseData.Step, index: number): SavedCourse.Step => {
    const res: SavedCourse.Step = {
      id: ignoreId ? undefined : step.id,
      stepOrder: index,
      lessonStepType: step.type,
    };

    switch (step.type) {
      case STEP_TYPE.CONVERSATION:
        assert(!!step.messages?.length, "Conversation step must have messages");
        res.stepMessages = step.messages.map(({ authorMessage, learnerMessage, order }) => ({
          authorMessage,
          learnerMessage,
          order,
        }));
        break;

      case STEP_TYPE.MCQ:
        assert(!!step.multipleChoice, "MCQ step must have multiple choice question");
        res.stepMultipleChoice = {
          question: step.multipleChoice.question,
          ...arrayToPrefixedDict(step.multipleChoice.options, "option", {
            minLength: 3,
            fallback: "",
          }),
          correctAnswer: step.multipleChoice.options[step.multipleChoice.correctAnswer],
        };
        break;

      case STEP_TYPE.FITB:
        assert(!!step.fillInTheBlank, "FITB step must have fill in the blank question");
        res.stepFillInTheBlank = {
          question: step.fillInTheBlank.question,
          ...arrayToPrefixedDict(step.fillInTheBlank.options, "option", {
            minLength: 3,
            fallback: "",
          }),
          correctAnswer: step.fillInTheBlank.options[step.fillInTheBlank.correctAnswer],
        };
        break;

      case STEP_TYPE.MATCHING:
        assert(!!step.matching, "Matching step must have matching question");
        res.stepMatching = {
          question: step.matching.question,
          ...arrayToPrefixedDict(
            step.matching.answers.map((p) => p.question),
            "option",
            {
              minLength: 3,
              fallback: "",
            },
          ),
          ...arrayToPrefixedDict(
            step.matching.answers.map((p) => p.answer),
            "answer",
            {
              minLength: 3,
              fallback: "",
            },
          ),
          correctAnswer: "string",
        };
        break;

      default:
        assert(false, "Invalid step type");
    }

    return res;
  };
}

function lessonToSavedLesson({ ignoreId }: CourseToSavedCourseOptions = {}) {
  return ({ titles, ...lesson }: CourseData.Lesson, i: number): SavedCourse.Lesson => {
    const res: SavedCourse.Lesson = {
      id: ignoreId ? undefined : lesson.id,
      lessonOrder: i,
      selectedTitle: titles[lesson.selectedTitle ?? 0],
      ...arrayToPrefixedDict(titles, "title"),
      lessonSteps: lesson.steps.map(stepToSavedStep({ ignoreId })),
    };
    return res;
  };
}

/**
 * Converts a course to a saved course format.
 * @param course - The course data to convert.
 * @param options - Options for the conversion.
 * @param options.ignoreId - Whether or not to expect IDs in the conversion.
 * @returns The converted saved course data.
 */
export function courseToSavedCourse(
  { chapters, category, subcategory, ...course }: CourseData.Course,
  { ignoreId = false }: CourseToSavedCourseOptions = {},
): SavedCourse.Course {
  const categoryId = subcategory?.id ?? category?.id;

  assert(!!course.title?.length, "Course title is required");
  assert(!!course.description?.length, "Course description is required");
  assert(!!course.targetAudience?.length, "Course target audience is required");
  assert(!!course.conversationTone?.length, "Course conversation tone is required");
  assert(!!course.aboutTheAuthor?.length, "Course about the author is required");
  assert(!!course.courseCharacters?.length, "Course must have at least one character");
  assert(!!chapters?.length, "Course must have at least one chapter");
  assert(!!categoryId, "Course category is required");
  assert(course.courseType !== undefined, "Course type is required");
  if (!ignoreId) {
    assert(!!course.id, "Course ID is required");
  }

  const fullCourse = course as Required<CourseData.Course>;
  const { t, tNum } = getI18n();

  const res = {
    ...fullCourse,

    categoryId: categoryId,
    units: chapters.map((unit, i) => {
      const res: SavedCourse.Unit = {
        id: unit.id,
        title: unit.title ?? `${t("Course.Chapter")} ${tNum(i + 1)}`,
        unitOrder: i,
        lessons: unit.lessons.map(lessonToSavedLesson({ ignoreId })),
      };

      return res;
    }),
  };

  return res;
}
